import React, {ReactNode, useCallback, useState} from 'react';
import debounce from 'lodash.debounce';

import {Title, Icon, PriorityIcon, Text} from '@priority-software/priority-style-react';
import {tooltipDelay} from '../../constants';
import {getTopDialogZindex} from '../../../../_shared/GWTBridge/invoke/priModalDlg';
import texts from '../../texts';
import styles from './styles.module.scss';
import {Tooltip} from '../../../../components/Common/Tooltip/index';
import {isRTL} from '../../../../Localization';

type Props = {
    children?: ReactNode;
    title?: ReactNode;
    entityName: string;
    isEmpty: boolean;
    entityId: string;
    onScreenOpen?: (formName: string, subFormName?: string) => void;
    openColumnDialog?: (entityId: string) => void;
    formName: string;
    subFormName?: string;
    defaultCollapsed?: boolean;
    onSectionCollapse?: (entityId: string, isOpen: boolean) => void;
};

export const Section = (props: Props) => {
    const {
        onScreenOpen,
        formName,
        subFormName,
        defaultCollapsed,
        entityId,
        openColumnDialog,
        onSectionCollapse
    } = props;
    const [open, setOpen] = useState(!defaultCollapsed);
    const rtl = isRTL();

    const toggleSection = useCallback(() => {
        const flag = !open;

        if (onSectionCollapse) onSectionCollapse(entityId, flag);
        setOpen(flag);
    }, [setOpen, open, onSectionCollapse, entityId]);

    const openScreen = useCallback(
        () => onScreenOpen(formName, subFormName),
        [formName, onScreenOpen, subFormName]
    );

    const openEdit = useCallback(() => openColumnDialog(entityId), [entityId, openColumnDialog]);

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <Tooltip
                    tooltip={open ? texts.collapse_btn_tooltip : texts.expand_btn_tooltip}
                    position={rtl ? 'top right' : 'top left'}
                    wrapperClassname={styles.tooltip}
                    delay={tooltipDelay}
                    zIndex={getTopDialogZindex()}
                >
                    <Icon
                        icon={open ? PriorityIcon.arrowBottom : PriorityIcon.arrowUp}
                        iconColor="secondary-500"
                        onClick={toggleSection}
                    />
                </Tooltip>

                <Title skin="h6" textColor="primary-700" componentClasses={styles.sectionTitle}>
                    {props.title}
                </Title>

                <div className={styles.actionIcons}>
                    <Tooltip
                        tooltip={texts.info_zoom_tooltip}
                        position={rtl ? 'top left' : 'top right'}
                        delay={tooltipDelay}
                        wrapperClassname={styles.tooltip}
                        zIndex={getTopDialogZindex()}
                    >
                        <Icon
                            icon={PriorityIcon.externalLink}
                            iconColor="primary-700"
                            onClick={debounce(openScreen, 150)}
                        />
                    </Tooltip>

                    <Tooltip
                        position={rtl ? 'top left' : 'top right'}
                        delay={tooltipDelay}
                        wrapperClassname={styles.editBtn}
                        zIndex={getTopDialogZindex()}
                        tooltip={texts.edit_btn_tooltip}
                    >
                        <Icon
                            icon={PriorityIcon.editFill}
                            iconColor="primary-700"
                            onClick={openEdit}
                        />
                    </Tooltip>
                </div>
            </div>

            {open && (
                <div className={styles.sectionContent}>
                    {props.isEmpty ? (
                        <div className={styles.emptyState}>
                            <Text tag="div" skin="paragraph-2" textColor="secondary-400">
                                {texts.no_details_msg}
                            </Text>
                        </div>
                    ) : (
                        props.children
                    )}
                </div>
            )}
        </div>
    );
};
